/**
 * LicenseGate / LicenseProvider - context global du statut licence.
 *
 * Plus de hard-redirect : si pas de licence, on bascule en TRIAL MODE.
 * Les composants enfants consomment useLicense() pour decider :
 *   - blurer les details techniques
 *   - desactiver les exports
 *   - afficher la banniere upgrade
 *
 * Les pages "exemptees" (settings, tenants, onboarding) ne sont pas affectees -
 * elles fonctionnent identique en trial / paid.
 */
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { clientLicenseApi, type ClientLicenseStatus } from '../api/licenseApi';
import { DEMO_MODE } from '../api/liveMode';
import { demoLicense } from '../demo/demoData';

interface LicenseContextValue {
  status:       ClientLicenseStatus | null;
  loading:      boolean;
  isTrial:      boolean;
  hasLicense:   boolean;
  isExpired:    boolean;
  refresh:      () => void;
}

const LicenseContext = createContext<LicenseContextValue>({
  status:     null,
  loading:    true,
  isTrial:    true,
  hasLicense: false,
  isExpired:  false,
  refresh:    () => {},
});

export function useLicense() { return useContext(LicenseContext); }

interface Props { children: ReactNode }

export function LicenseGate({ children }: Props) {
  const location = useLocation();
  const [status, setStatus]   = useState<ClientLicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick]       = useState(0);

  useEffect(() => {
    // Mode démo : licence Business mockée, aucun appel réseau.
    if (DEMO_MODE) { setStatus(demoLicense()); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    clientLicenseApi.status()
      .then(s => { if (!cancelled) setStatus(s); })
      .catch(() => { if (!cancelled) setStatus(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [location.pathname, tick]);

  const hasLicense = Boolean(status?.hasLicense);
  const isExpired  = Boolean(status?.expired);
  const isTrial    = !hasLicense || isExpired;

  const value: LicenseContextValue = {
    status,
    loading,
    isTrial,
    hasLicense,
    isExpired,
    refresh: () => setTick(t => t + 1),
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
}
